import type { CurrentRefinementsConnectorParamsItem } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import type { ReactElement, CSSProperties } from 'react';

import { XIcon } from '@phosphor-icons/react';
import { useRouter } from 'next/compat/router';
import { useMemo, useCallback } from 'react';
import { useClearRefinements } from 'react-instantsearch';

import { AF } from './styles';
import { COLOUR } from '~/utilities/constants/algolia';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { useAlgoliaFacets } from '~/utilities/hooks/search/useAlgoliaFacets';
import { useColorCode } from '~/utilities/hooks/useColorCode';

const MAX_SELECTED_FACETS = 3;

interface AppliedFilterProps extends CurrentRefinementsConnectorParamsItem {
  className?: string;
  style?: CSSProperties;
  tabIndex?: number;
  'aria-hidden'?: boolean;
}

export const AppliedFilter = ({
  attribute,
  refinements,
  className,
  style,
  tabIndex,
  'aria-hidden': ariaHidden,
}: AppliedFilterProps): ReactElement => {
  const getColorHex = useColorCode();
  const { canRefine, refine } = useClearRefinements({ includedAttributes: [attribute] });
  const router = useRouter();
  const { categoryDetails } = useProductListContext();

  const clearRefinement = async () => {
    const [facetName] = attribute.split('.');
    const isFacetSelectedInSolr = categoryDetails?.selectedFacetsV2?.some(
      facet => facet?.code === facetName
    );

    if (!isFacetSelectedInSolr && canRefine) {
      refine();

      return;
    }

    const onlyRefinement = refinements.at(0);
    const currentShopUrl = router?.asPath ?? '';
    const [currentShopPath, currentShopQuery] = currentShopUrl.split('?');

    const currentAppliedFacet = categoryDetails?.visibleFacetsV2?.find(
      facet => facet?.code === facetName
    );

    const facetUrlToRemove = currentAppliedFacet?.facetValues
      ?.find(facet => facet?.code === onlyRefinement?.label)
      ?.url?.split('?')
      .at(0);

    if (facetUrlToRemove === currentShopPath) {
      let url = facetUrlToRemove;

      if (currentShopQuery) {
        if (facetUrlToRemove?.includes('?')) {
          url = `${url}&${currentShopQuery}`;
        } else {
          url = `${url}?${currentShopQuery}`;
        }
      }

      if (url) {
        await router?.push(url, url, { shallow: false });
      }
    } else if (canRefine) {
      refine();
    }
  };
  const facets = useAlgoliaFacets();

  const sizeLabels = useMemo(
    () => ({
      size: `${facets.find(facet => facet.attribute === 'size')?.text}:`,
      length: `${facets.find(facet => facet.attribute === 'length')?.text}:`,
    }),
    [facets]
  );

  const computedValues = useMemo(() => {
    const hasMoreThanMax = refinements.length > MAX_SELECTED_FACETS;
    const slicedFacets = refinements.slice(0, Math.min(refinements.length, MAX_SELECTED_FACETS));
    const appliedTextLabel = slicedFacets
      .map(refinement => refinement.label)
      .join(', ')
      .concat(refinements.length > MAX_SELECTED_FACETS ? ', ...' : '');

    return { hasMoreThanMax, slicedFacets, appliedTextLabel };
  }, [refinements]);

  const { hasMoreThanMax, slicedFacets, appliedTextLabel } = computedValues;

  const isColorAttribute = useMemo(() => attribute.includes(COLOUR), [attribute]);
  const isSizeAttribute = useMemo(
    () => attribute === 'size' || attribute === 'length',
    [attribute]
  );

  const textFilter = <AF.AppliedFilterLabel>{appliedTextLabel}</AF.AppliedFilterLabel>;

  const renderColorLabel = useCallback(
    (refinement: { label: string }, index: number) => {
      const hexColor = getColorHex(refinement.label);

      return (
        <AF.AppliedFilterLabel
          key={index}
          $bgColor={hexColor}
          className="colorFilter"
          $hasBorder={hexColor.toLowerCase() === '#ffffff'}
        >
          {refinement.label}
        </AF.AppliedFilterLabel>
      );
    },
    [getColorHex]
  );

  const colorsFilter = useMemo(
    () => (
      <>
        {slicedFacets.map(renderColorLabel)}
        {hasMoreThanMax && (
          <AF.AppliedFilterLabel className="colorFilter" $height="16px">
            ...
          </AF.AppliedFilterLabel>
        )}
      </>
    ),
    [slicedFacets, hasMoreThanMax, renderColorLabel]
  );

  return (
    <AF.AppliedFilterWrapper className={className} style={style} aria-hidden={ariaHidden}>
      <AF.AppliedFilter
        onClick={clearRefinement}
        data-testid={`applied-filter-${attribute.split('.')[0]}`}
        tabIndex={tabIndex}
      >
        {isSizeAttribute && <AF.Label>{sizeLabels[attribute as keyof typeof sizeLabels]}</AF.Label>}
        {isColorAttribute ? colorsFilter : textFilter}
        <AF.RemoveAppliedIconWrapper>
          <XIcon />
        </AF.RemoveAppliedIconWrapper>
      </AF.AppliedFilter>
    </AF.AppliedFilterWrapper>
  );
};
